import type { SessionListItem } from '@/features/sessions/types';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import type { QuerySessionResponse } from '@heidi/session/contracts';
import { useQuerySessionById } from '@heidi/session/data';
import { useMemo } from 'react';

export type WorkspaceSession = Pick<
  SessionListItem,
  | 'session_id'
  | 'session_name'
  | 'patient'
  | 'patient_profile_id'
  | 'patient_profile_name'
  | 'patient_profile_appointment_candidates'
>;

const toWorkspaceSession = (session: QuerySessionResponse): WorkspaceSession => {
  const profile = session.patient_profile;
  const profileName = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return {
    session_id: session.session_id,
    session_name: session.session_name,
    patient: { name: session.patient?.name },
    patient_profile_id: profile?.id,
    patient_profile_name: profileName || null,
    patient_profile_appointment_candidates:
      session.patient_profile_appointment_candidates,
  };
};

/** Direct read for a deep-linked session outside the first page of the recent feeds. */
export function useObjectWorkspaceSessionLookup(sessionId: string | undefined) {
  const query = useQuerySessionById({ sessionId: sessionId ?? '' });
  const isNotFound = query.isError && isSessionNotFoundError(query.error);
  const session = useMemo(
    () => (query.data ? toWorkspaceSession(query.data) : undefined),
    [query.data]
  );

  return {
    session,
    isMissing: isNotFound || (query.isSuccess && !query.data),
    isError: query.isError && !isNotFound,
    refetch: query.refetch,
  };
}
